import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { morphedRead } from "../../auth/morphed-client";
import { resolveEndpoint } from "../../auth/morphed-client";
import { loadEntityDefinitions, type EntityDefinition } from "../../services/entity-definition.service";
import { resolveDisplayKeys } from "../../utils/relation-display";

interface RelationItem {
	id: string;
	label: string;
	secondary: string[];
}

@customElement("clam-relation-autocomplete")
export class ClamRelationAutocomplete extends LitElement {
	static styles = css`
		:host { display: block; font-family: var(--clam-font); }
		.input-wrap { position: relative; }
		input {
			width: 100%; padding: var(--clam-space-2, 8px) var(--clam-space-3, 12px); border: 1px solid var(--clam-border, #ddd); border-radius: var(--clam-radius-sm, 6px);
			font-size: var(--clam-text-base, 14px); box-sizing: border-box; font-family: var(--clam-font);
		}
		input:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
		.dropdown {
			position: absolute; top: 100%; left: 0; right: 0; z-index: 50;
			background: var(--clam-surface, #fff); border: 1px solid var(--clam-border, #e0e0e0); border-radius: 0 0 var(--clam-radius-md, 8px) var(--clam-radius-md, 8px);
			box-shadow: var(--clam-shadow-md, 0 4px 12px rgba(0,0,0,0.1)); max-height: 250px; overflow-y: auto;
		}
		.opt { padding: var(--clam-space-2, 8px) var(--clam-space-3, 12px); cursor: pointer; font-size: var(--clam-text-sm, 13px); border-bottom: 1px solid var(--clam-almost-white, #f5f5f5); }
		.opt:hover { background: var(--clam-surface-soft, #f5f5f5); }
		.opt-main { font-weight: 500; color: var(--clam-text, #333); }
		.opt-sub { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #888); margin-top: 2px; }
		.chips { display: flex; flex-wrap: wrap; gap: var(--clam-space-1, 4px); margin-bottom: var(--clam-space-1, 4px); }
		.chip {
			display: inline-flex; align-items: center; gap: 4px;
			padding: 3px var(--clam-space-2, 8px); background: rgba(90,157,30,0.1); border-radius: var(--clam-radius-sm, 8px); font-size: var(--clam-text-xs, 12px); color: var(--clam-green-dark, #1565c0);
		}
		.chip button { background: none; border: none; cursor: pointer; color: var(--clam-light-gray, #999); font-size: 16px; padding: 0 4px; min-width: 28px; min-height: 28px; line-height: 1; border-radius: var(--clam-radius-sm, 6px); }
		.no-results { padding: var(--clam-space-3, 12px); color: var(--clam-light-gray, #999); font-size: var(--clam-text-sm, 13px); text-align: center; }
	`;

	@property() relatedEntity = "";
	@property() displayKey = "";
	@property({ type: Array }) secondaryKeys: string[] = [];
	@property({ type: Boolean }) multi = false;
	@property() placeholder = "Buscar...";
	@property({ attribute: false }) value: string | string[] | null = null;

	private items: RelationItem[] = [];
	private filtered: RelationItem[] = [];
	private open = false;
	private query = "";
	private selectedLabels: Map<string, RelationItem> = new Map();
	private _retried = false;

	async connectedCallback() {
		super.connectedCallback();
		await this.loadData();
		this.updateSelectedLabels();
		this.requestUpdate();
	}

	private async loadData() {
		try {
			const defs = await loadEntityDefinitions();
			const def = defs.find((d) => d.entityName === this.relatedEntity);
			const endpoint = def?.entityEndpoint || resolveEndpoint(this.relatedEntity);
			// Auto-resolver displayKey/secondaryKeys si el caller no los fija (ej. Clam Evento → eventName).
			if (!this.displayKey) {
				const resolved = resolveDisplayKeys(def);
				this.displayKey = resolved.displayKey;
				if (this.secondaryKeys.length === 0) this.secondaryKeys = resolved.secondaryKeys;
			}
			const records = await morphedRead<Record<string, unknown>>(this.relatedEntity, {
				options: { limit: 500 },
				allowPublic: def?.public ?? true,
			});
			this.items = records.map((r) => ({
				id: r.id as string,
				label: String(r[this.displayKey] || r.name || r.email || r.id).substring(0, 60),
				secondary: this.secondaryKeys
					.map((k) => (r[k] ? String(r[k]).substring(0, 30) : ""))
					.filter(Boolean),
			}));
			this.filtered = [...this.items].slice(0, 50);
			this.updateSelectedLabels();
			this.requestUpdate();
			// Reintento unico por timing (si el registro de endpoints todavia cargaba)
			if (this.items.length === 0 && !this._retried) {
				this._retried = true;
				setTimeout(() => { void this.loadData(); }, 600);
			}
		} catch (e) {
			console.error(`relation-autocomplete: failed to load ${this.relatedEntity}:`, e);
		}
	}

	private updateSelectedLabels() {
		this.selectedLabels.clear();
		const ids = Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
		for (const id of ids) {
			const item = this.items.find((i) => i.id === id);
			if (item) this.selectedLabels.set(id, item);
		}
	}

	private onInput(e: InputEvent) {
		this.query = (e.target as HTMLInputElement).value.toLowerCase();
		this.filtered = this.items
			.filter((i) => i.label.toLowerCase().includes(this.query) || i.secondary.some((s) => s.toLowerCase().includes(this.query)))
			.slice(0, 50);
		this.open = true;
		this.requestUpdate();
	}

	private choose(item: RelationItem) {
		if (this.multi) {
			const current = Array.isArray(this.value) ? [...this.value] : [];
			if (current.includes(item.id)) {
				const idx = current.indexOf(item.id);
				current.splice(idx, 1);
				this.selectedLabels.delete(item.id);
			} else {
				current.push(item.id);
				this.selectedLabels.set(item.id, item);
			}
			this.value = current;
		} else {
			this.value = item.id;
			this.selectedLabels.set(item.id, item);
			this.open = false;
		}
		this.dispatchEvent(new CustomEvent("change", { detail: this.value }));
		this.requestUpdate();
	}

	private removeItem(id: string) {
		if (this.multi) {
			this.value = (Array.isArray(this.value) ? this.value : []).filter((v) => v !== id);
			this.selectedLabels.delete(id);
		} else {
			this.value = null;
			this.selectedLabels.clear();
		}
		this.dispatchEvent(new CustomEvent("change", { detail: this.value }));
		this.requestUpdate();
	}

	render() {
		const selectedIds = Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
		return html`
		${this.multi && selectedIds.length > 0 ? html`
			<div class="chips">
				${selectedIds.map((id) => {
					const item = this.selectedLabels.get(id);
					return html`
						<span class="chip">
							${item
								? html`${item.label}${item.secondary.length ? html` <span style="opacity:.7">· ${item.secondary[0]}</span>` : ''}`
								: id.substring(0, 8)}
							<button @click=${() => this.removeItem(id)}>×</button>
						</span>
					`;
				})}
			</div>
		` : null}
			<div class="input-wrap">
				<input
					type="text"
					placeholder=${this.multi || !this.value ? this.placeholder : (this.selectedLabels.get(selectedIds[0])?.label || this.placeholder)}
					.value=${this.open ? this.query : ""}
					@input=${(e: InputEvent) => this.onInput(e)}
					@focus=${() => { this.open = true; this.requestUpdate(); }}
					@blur=${() => setTimeout(() => { this.open = false; this.requestUpdate(); }, 200)}
				/>
				${this.open && this.filtered.length > 0 ? html`
					<div class="dropdown">
						${this.filtered.map((item) => html`
							<div class="opt" @mousedown=${(e: Event) => { e.preventDefault(); this.choose(item); }}>
								<div class="opt-main">${item.label}</div>
								${item.secondary.length > 0 ? html`<div class="opt-sub">${item.secondary.join(" · ")}</div>` : null}
							</div>
						`)}
					</div>
				` : this.open && this.filtered.length === 0 ? html`
					<div class="dropdown"><div class="no-results">Sin resultados</div></div>
				` : null}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"clam-relation-autocomplete": ClamRelationAutocomplete;
	}
}
